import { inject, Injectable } from '@angular/core';
import * as EmailValidator from 'email-validator';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import { NGXLogger } from 'ngx-logger';

import { TolerantPostViewModelMapper } from '../../clients/tolerant-post/mapper/view-model.mapper';
import { SuggestedAddress } from '../../clients/tolerant-post/model/view.model';
import { AddressField } from '../model/address-field.model';
import { CommunicationField } from '../model/communication-field.model';
import { CustomerAddress } from '../model/customer-information.model';
import { Customer } from '../model/customer.model';
import { hasForbiddenNameCharacters } from '../model/gcdm-constraints';
import { IdentityField } from '../model/identity-field.model';
import { AddressViewService } from './address.view-service';
import { FeatureVisibilityService } from './feature-visibility.service';
import { TaxNumberValidationService } from './tax-number-validation.service';

export enum AddressStatus {
  VALID = 'VALID',
  INVALID = 'INVALID',
}

export interface AddressValidityStatus {
  status: AddressStatus;
  invalidFields: AddressField[];
  usedAddressForValidation?: SuggestedAddress;
}

/**
 * This service is responsible for validation customer information
 */
@Injectable({
  providedIn: 'root',
})
export class CustomerValidationService {
  private readonly tolerantMapper = inject(TolerantPostViewModelMapper);
  private readonly addressViewService = inject(AddressViewService);
  private readonly taxNumberValidationService = inject(TaxNumberValidationService);
  private readonly featureVisibilityService = inject(FeatureVisibilityService);
  private readonly logger = inject(NGXLogger);

  private readonly phoneUtil: PhoneNumberUtil = PhoneNumberUtil.getInstance();

  validateCommunicationInformation(
    phone: string | undefined,
    mobilePhone: string | undefined,
    email: string | undefined,
    country: string | undefined,
    isPrimaryData = true,
  ) {
    const invalidCommunicationFields: CommunicationField[] = [];
    if ((!email || !EmailValidator.validate(email)) && (isPrimaryData || email)) {
      invalidCommunicationFields.push(CommunicationField.EMAIL);
    }

    if (phone && !this.isPhoneNumberValid(phone, country)) {
      invalidCommunicationFields.push(CommunicationField.PHONE);
    }

    if (mobilePhone && !this.isPhoneNumberValid(mobilePhone, country)) {
      invalidCommunicationFields.push(CommunicationField.MOBILE_PHONE);
    }

    return invalidCommunicationFields;
  }

  normalizePhoneNumber(phoneNumberString: string | undefined): string | undefined {
    const cleanedPhoneNumber = phoneNumberString?.replace(/[.,\-\s]/g, '');
    if (cleanedPhoneNumber?.startsWith('+')) {
      // separate country prefix and national suffix by a space. (e.g. "+49 891234567")
      const phoneObj = this.phoneUtil.parse(phoneNumberString);
      const e164 = this.phoneUtil.format(phoneObj, PhoneNumberFormat.E164); // e.g. "+49891234567"
      const countryPrefix = phoneObj.getCountryCode(); // e.g. "49"
      return e164.replace(`+${countryPrefix}`, `+${countryPrefix} `); // add space
    }
    return cleanedPhoneNumber;
  }

  isPhoneNumberValid(phoneNumberString: string, country: string | undefined): boolean {
    const hasLetters = /[a-zA-Z]/.test(phoneNumberString);

    if (hasLetters) {
      return false;
    }

    try {
      const phoneNumber = this.phoneUtil.parse(phoneNumberString, country);
      return this.phoneUtil.isValidNumber(phoneNumber);
    } catch (e) {
      this.logger.info(`#CDFTF0119 Invalid phone number. See reason:`, e);
      return false;
    }
  }

  isPhoneNumberValidWithoutCountry(phoneNumber: string | undefined): boolean {
    if (phoneNumber === undefined || phoneNumber.trim() === '') {
      return true;
    }
    const hasLetters = /[a-zA-Z]/.test(phoneNumber);
    if (hasLetters) {
      return false;
    }

    const normalizedPhoneNumber = phoneNumber.replace(/[^0-9+]/g, '');
    if (normalizedPhoneNumber.length < 7 || normalizedPhoneNumber.length > 15) {
      return false;
    }
    if (!/^[0-9+]+$/.test(normalizedPhoneNumber)) {
      return false;
    }

    // enforce that the phone number starts with a plus sign
    const plusCount = normalizedPhoneNumber.split('+').length - 1;
    return !(plusCount !== 1 || normalizedPhoneNumber.indexOf('+') !== 0);
  }

  /**   * @param identityLocked true when the name fields are rendered read-only, i.e. the profile edit
   * dialog (`editingIdentityDisabled`). The GCDM character rules are then skipped for them:
   * a value the user cannot change must not block the save of the fields they can change.
   * Retail CRM enforces the same pattern since its own GCDM integration, so a violating name
   * can only come from legacy data - and blocking it here would leave the whole customer
   * uneditable, with the offending field greyed out.
   */

  validateIdentityInformation(customer: Customer, identityLocked = false) {
    const invalidIdentityFields: IdentityField[] = [];

    if (!customer.salutation) {
      invalidIdentityFields.push(IdentityField.SALUTATION);
    }
    if (!customer.firstName) {
      invalidIdentityFields.push(IdentityField.FIRST_NAME);
    }
    if (!customer.lastName) {
      invalidIdentityFields.push(IdentityField.LAST_NAME);
    }
    // GCDM rejects a fixed set of special characters in the name fields. Only reported for
    // non-empty values - an empty mandatory field is already covered above - and only where the
    // user can act on it, see identityLocked.
    if (!identityLocked) {
      if (hasForbiddenNameCharacters(customer.firstName)) {
        invalidIdentityFields.push(IdentityField.FIRST_NAME);
      }
      if (hasForbiddenNameCharacters(customer.middleName)) {
        invalidIdentityFields.push(IdentityField.MIDDLE_NAME);
      }
      if (hasForbiddenNameCharacters(customer.lastName)) {
        invalidIdentityFields.push(IdentityField.LAST_NAME);
      }
      if (hasForbiddenNameCharacters(customer.suffix)) {
        invalidIdentityFields.push(IdentityField.SUFFIX);
      }
    }
    if (!this.taxNumberValidationService.isTaxNumberValid(customer.taxNumber, customer.taxNumberCountry)) {
      invalidIdentityFields.push(IdentityField.TAX_NUMBER);
    }
    if (
      !customer.correspondenceLanguage &&
      !this.featureVisibilityService.isHidden(IdentityField.CORRESPONDENCE_LANGUAGE)
    ) {
      invalidIdentityFields.push(IdentityField.CORRESPONDENCE_LANGUAGE);
    }
    // GCDM's PersonalIdentification requires the id and its type together, so CDF must not send
    // one alone. Only reported when the fields are visible: a market that hides them gives the
    // user no way to fix the pair.
    if (!this.featureVisibilityService.isHidden(IdentityField.NATIONAL_ID)) {
      if (customer.nationalId1DocumentType && !customer.nationalId1) {
        invalidIdentityFields.push(IdentityField.NATIONAL_ID);
      }
      if (customer.nationalId1 && !customer.nationalId1DocumentType) {
        invalidIdentityFields.push(IdentityField.NATIONAL_ID_DOCUMENT_TYPE);
      }
    }
    return invalidIdentityFields;
  }

  getAddressValidity(address: CustomerAddress, suggestedAddresses: SuggestedAddress[]): AddressValidityStatus {
    const highestSuggestedAddress: SuggestedAddress | undefined = this.findHighestQualityAddress(suggestedAddresses);
    if (!highestSuggestedAddress) {
      this.logger.info('No suggestions were fetched from Tolerant Post. Marking address as invalid.');
      return { status: AddressStatus.INVALID, invalidFields: this.addressViewService.getAllAddressFields() };
    }

    if (Number(highestSuggestedAddress.quality) === 100) {
      // With the given address the suggestion is 100% accurate. Even if some fields differ the intended location is still obvious.
      return { status: AddressStatus.VALID, invalidFields: [], usedAddressForValidation: highestSuggestedAddress };
    }

    // The PROVINCE field is excluded from the address validation computation, as it is automatically filled using suggestions from Tolerant Post.
    // ADDRESS_LINE_1 and ADDRESS_LINE_2 are excluded, as they should never be validated
    const relevantDifferingFields = this.getDifferingFields(address, highestSuggestedAddress).filter(
      (field) =>
        field !== AddressField.PROVINCE &&
        field !== AddressField.ADDRESS_LINE_1 &&
        field !== AddressField.ADDRESS_LINE_2 &&
        field !== AddressField.POST_BOX,
    );

    return {
      status: relevantDifferingFields.length > 0 ? AddressStatus.INVALID : AddressStatus.VALID,
      invalidFields: relevantDifferingFields,
      usedAddressForValidation: highestSuggestedAddress,
    };
  }

  findHighestQualityAddress(addresses: SuggestedAddress[]): SuggestedAddress | undefined {
    return addresses.reduce<SuggestedAddress | undefined>((max, address) => {
      if (max === undefined) {
        return address;
      }
      const currentScore = Number(address.quality);
      const maxScore = Number(max.quality);
      return currentScore > maxScore ? address : max;
    }, undefined);
  }

  getDifferingFields(address: CustomerAddress, suggestedAddress: SuggestedAddress): AddressField[] {
    const suggestedCustomerAddress = this.tolerantMapper.mapToCustomerAddress(suggestedAddress);
    const differingKeys = this.getDifferingKeys(suggestedCustomerAddress, address);

    return differingKeys.map((key) => {
      switch (key) {
        case 'postalCode':
          return AddressField.POSTAL_CODE;
        case 'province':
          return AddressField.PROVINCE;
        case 'city':
          return AddressField.CITY;
        case 'houseNumber':
          return AddressField.HOUSE_NUMBER;
        case 'street':
          return AddressField.STREET;
        case 'country':
          return AddressField.COUNTRY;
        case 'addressLine1':
          return AddressField.ADDRESS_LINE_1;
        case 'addressLine2':
          return AddressField.ADDRESS_LINE_2;
        case 'postBox':
          return AddressField.POST_BOX;
      }
    });
  }

  getInvalidFields(address: CustomerAddress | undefined, suggestions: SuggestedAddress[]): AddressField[] {
    if (!address) {
      return this.allAddressFields();
    }

    const validityStatus = this.getAddressValidity(address, suggestions);
    return validityStatus.status === AddressStatus.INVALID ? validityStatus.invalidFields : [];
  }

  /**
   * Does compare the equality of two objects.
   * Fields are equal, even if one value is an empty string and the value of the other object is undefined.
   * @returns list of keys of objects as strings which do not have the same value
   */
  getDifferingKeys<T extends object>(obj1: T, obj2: T): (keyof T)[] {
    return Object.keys(obj1).filter((key) => {
      const value1 = obj1[key as keyof T];
      const value2 = obj2[key as keyof T];
      return value1 !== value2 && !(value1 === '' && value2 === undefined) && !(value1 === undefined && value2 === '');
    }) as (keyof T)[];
  }

  allAddressFields() {
    return Object.values(AddressField);
  }

  /**
   * @returns true if any address information other than country is set. Returns false if only country or no fields are defined
   */
  isRelevantAddressInformationSet(address: CustomerAddress) {
    return Object.entries(address).some(([key, value]) => key !== 'country' && value !== undefined && value !== '');
  }
}
